import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { money } from './drawerFormat.js';
import {
  compactBindingLabel,
  exportLifecycleLabel,
  laneLabel,
} from './varianceActionDisplay.js';
import {
  ALL,
  bindingHoverLabel,
  bindingTextSx,
  isExportedAction,
} from './exportPreviewHelpers.js';
import {
  BooleanStatusIcon,
  MetricBlock,
  MutationIcon,
  ProductCodeCell,
} from './ExportPreviewCells.jsx';
import { downloadBillingReconciliationExportArtifactFile } from '../../hooks/api/useBillingReconciliationHooks.js';

const pluralize = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const disabledExportReason = ({
  canExport,
  cycleClose = {},
  draftCount = 0,
  emptyReason,
  hasRows = true,
  label,
  blockerCount = 0,
  warningCount = 0,
  includeDraftContext = false,
}) => {
  if (canExport) return '';
  if (!hasRows && includeDraftContext && draftCount > 0) {
    return `${label} has ${pluralize(draftCount, 'unsaved draft action')} that will not be exported until saved.`;
  }
  if (!hasRows) return emptyReason || `${label} has no exportable rows yet.`;
  if (cycleClose.gmTotalError) return `${label} is blocked because the modified GM total is unavailable.`;
  const reasons = [];
  if (blockerCount > 0) reasons.push(`${pluralize(blockerCount, 'export blocker')}. Resolve blockers before export.`);
  if (warningCount > 0) reasons.push(`${pluralize(warningCount, 'validation warning')}. Resolve warnings before export.`);
  if (reasons.length) {
    const draftSuffix = includeDraftContext && draftCount > 0
      ? ` ${pluralize(draftCount, 'draft action')} will not be exported until saved.`
      : '';
    return `${label} is blocked by ${reasons.join(' ')}${draftSuffix}`;
  }
  if (includeDraftContext && draftCount > 0) {
    return `${label} has ${pluralize(draftCount, 'unsaved draft action')} that will not be exported until saved.`;
  }
  return emptyReason || `${label} is not available yet.`;
};

const ExportToolbarButton = ({
  canExport,
  children,
  disabledReason,
  label,
  ...buttonProps
}) => {
  const reason = !canExport ? disabledReason : '';
  const reasonId = reason ? `billing-export-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-disabled-reason` : undefined;
  return (
    <Tooltip title={reason || ''} disableHoverListener={!reason} disableFocusListener={!reason} disableTouchListener={!reason}>
      <span>
        <Button
          {...buttonProps}
          aria-describedby={reasonId}
          data-disabled-reason={reason || undefined}
          disabled={!canExport}
        >
          {children}
        </Button>
        {reason ? (
          <span id={reasonId} hidden>
            {reason}
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
};

export const ExportPreviewToolbar = ({
  binding,
  blockers,
  canDownloadActionCsv,
  canExportCpq,
  canExportGm,
  cycleClose,
  deltaValue,
  draftCount: draftCountProp = 0,
  exportsQuery,
  handleActionCsv,
  handleBundle,
  handleExport,
  handleLineageCsv,
  readiness = {},
  setBinding,
  setPage,
  summary = {},
}) => {
  const unassignedCpqCount = Number(cycleClose.unassignedCpqBillableCount || 0);
  const unassignedCpqLabel = unassignedCpqCount === 1 ? 'missing-BAC row' : 'missing-BAC rows';
  const draftCount = Number(draftCountProp || 0);
  const laneBlockers = (lane) => blockers.filter((blocker) => blocker.lane === lane);
  const laneWarnings = (lane) => (readiness.warnings || []).filter((warning) => !warning.lane || warning.lane === lane);
  const gmLane = readiness.lanes?.gm_correction || {};
  const cpqLane = readiness.lanes?.cpq_peoplesoft_adjustment || {};
  const gmLaneRows = Number(gmLane.actionCount ?? summary.byLane?.gm_correction ?? 0);
  const cpqLaneRows = Number(cpqLane.actionCount ?? summary.byLane?.cpq_peoplesoft_adjustment ?? 0);
  const savedActionCount = Number(summary.total || 0);
  const hasExportRows = Boolean(exportsQuery.data?.rows?.length);
  const gmDisabledReason = disabledExportReason({
    canExport: canExportGm,
    cycleClose,
    draftCount,
    hasRows: gmLaneRows > 0,
    blockerCount: laneBlockers('gm_correction').length,
    warningCount: laneWarnings('gm_correction').length,
    label: 'Retained GM XLSX',
  });
  const cpqDisabledReason = disabledExportReason({
    canExport: canExportCpq,
    draftCount,
    hasRows: cpqLaneRows > 0,
    blockerCount: laneBlockers('cpq_peoplesoft_adjustment').length,
    warningCount: laneWarnings('cpq_peoplesoft_adjustment').length,
    label: 'Retained CPQ XLSX',
  });
  const actionCsvDisabledReason = disabledExportReason({
    canExport: canDownloadActionCsv,
    draftCount,
    hasRows: savedActionCount > 0,
    blockerCount: blockers.length,
    warningCount: (readiness.warnings || []).length,
    includeDraftContext: true,
    label: 'Action CSV',
  });
  const lineageDisabledReason = disabledExportReason({
    canExport: hasExportRows,
    emptyReason: 'Lineage CSV is available after at least one export has been generated.',
    label: 'Lineage CSV',
  });
  const zipDisabledReason = disabledExportReason({
    canExport: hasExportRows,
    emptyReason: 'ZIP is available after at least one export has been generated.',
    label: 'ZIP',
  });
  return (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
      <Stack direction="row" spacing={{ xs: 2, md: 3 }} useFlexGap flexWrap="wrap">
        <MetricBlock
          label="Modified GM"
          value={cycleClose.gmTotalError ? '--' : money(cycleClose.modifiedGmTotal || 0)}
          color={cycleClose.gmTotalError ? 'error.main' : 'success.main'}
          helper={cycleClose.gmTotalError ? 'Unavailable' : 'Retained file total'}
        />
        <MetricBlock
          label="Projected CPQ"
          value={money(cycleClose.projectedCpqBillableTotal || 0)}
          color="info.main"
          helper={unassignedCpqCount > 0
            ? `Excludes ${unassignedCpqCount} ${unassignedCpqLabel} (${money(cycleClose.unassignedCpqBillableTotal || 0)})`
            : 'This month billing'}
        />
        <MetricBlock
          label="Delta"
          value={cycleClose.gmTotalError ? '--' : money(deltaValue)}
          color={!cycleClose.gmTotalError && Math.abs(deltaValue) < 0.01 ? 'success.main' : 'warning.main'}
          helper="GM minus CPQ"
        />
        <MetricBlock
          label="Blockers"
          value={blockers.length}
          color={blockers.length ? 'error.main' : 'text.primary'}
          helper={blockers.length ? 'Resolve before export' : 'Ready'}
        />
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="export-binding-label">Binding</InputLabel>
          <Select
            labelId="export-binding-label"
            value={binding}
            label="Binding"
            onChange={(event) => {
              setBinding(event.target.value);
              setPage(0);
            }}
          >
            <MenuItem value={ALL}>All binding</MenuItem>
            <MenuItem value="warning">Any warning</MenuItem>
            <MenuItem value="missing_evidence">Missing evidence</MenuItem>
            <MenuItem value="missing_account">Missing Salesforce account</MenuItem>
            <MenuItem value="both">Missing both</MenuItem>
            <MenuItem value="linked">Fully linked</MenuItem>
          </Select>
        </FormControl>
        <ExportToolbarButton
          canExport={canExportGm}
          disabledReason={gmDisabledReason}
          label="Retained GM XLSX"
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => handleExport('gm_correction')}
        >
          Retained GM XLSX
        </ExportToolbarButton>
        <ExportToolbarButton
          canExport={canExportCpq}
          disabledReason={cpqDisabledReason}
          label="Retained CPQ XLSX"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => handleExport('cpq_peoplesoft_adjustment')}
        >
          Retained CPQ XLSX
        </ExportToolbarButton>
        <ExportToolbarButton
          canExport={canDownloadActionCsv}
          disabledReason={actionCsvDisabledReason}
          label="Action CSV"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleActionCsv}
        >
          Action CSV
        </ExportToolbarButton>
        <ExportToolbarButton
          canExport={hasExportRows}
          disabledReason={lineageDisabledReason}
          label="Lineage CSV"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleLineageCsv}
        >
          Lineage CSV
        </ExportToolbarButton>
        <ExportToolbarButton
          canExport={hasExportRows}
          disabledReason={zipDisabledReason}
          label="ZIP"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleBundle}
        >
          ZIP
        </ExportToolbarButton>
      </Stack>
    </Stack>
  </Paper>
  );
};

export const ExportBlockersAlert = ({ blockers, onOpenBac }) => {
  if (!blockers.length) return null;
  return (
    <Alert severity="error">
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Export blockers
      </Typography>
      <Stack spacing={0.5}>
        {blockers.slice(0, 8).map((blocker) => (
          <Stack
            key={`${blocker.actionId}-${blocker.code}`}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2">
              {blocker.bac} {blocker.productCode || blocker.productName || 'Product'} - {blocker.message}
            </Typography>
            {onOpenBac && blocker.bac && (
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                onClick={() => onOpenBac(blocker.bac, {
                  source: 'export_blocker',
                  severity: 'error',
                  code: blocker.code,
                  lane: blocker.lane,
                  mutation: blocker.mutation,
                  title: 'Export blocker',
                  summary: blocker.message,
                  detail: blocker.productCode || blocker.productName || blocker.actionId,
                  actionId: blocker.actionId,
                  focusActionId: blocker.actionId,
                  productCode: blocker.productCode,
                  productName: blocker.productName,
                })}
              >
                Open
              </Button>
            )}
          </Stack>
        ))}
        {blockers.length > 8 && (
          <Typography variant="caption">{blockers.length - 8} more blocker{blockers.length - 8 === 1 ? '' : 's'} hidden in this panel.</Typography>
        )}
      </Stack>
    </Alert>
  );
};

export const ExportHistoryPanel = ({ exportsQuery, runId }) => {
  if (!exportsQuery.data?.rows?.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          Export history
        </Typography>
        <Stack spacing={0.75}>
          {exportsQuery.data.rows.slice(0, 4).map((record) => {
            const workbookArtifact = record.artifacts?.find((artifact) => artifact.kind === 'workbook');
            const csvArtifact = record.artifacts?.find((artifact) => artifact.kind === 'csv');
            const lineageArtifact = record.artifacts?.find((artifact) => artifact.kind === 'lineage');
            const manifestArtifact = record.artifacts?.find((artifact) => artifact.kind === 'manifest');
            const zipArtifact = record.artifacts?.find((artifact) => artifact.kind === 'zip');
            return (
              <Stack
                key={record.id}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {laneLabel(record.lane)} - {new Date(record.createdAt).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {record.actionCount || 0} actions - {record.contentHash?.slice(0, 10) || 'no hash'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75}>
                  {workbookArtifact && (
                    <Button size="small" onClick={() => downloadBillingReconciliationExportArtifactFile({ runId, exportId: record.id, kind: 'workbook' })}>XLSX</Button>
                  )}
                  {csvArtifact && (
                    <Button size="small" onClick={() => downloadBillingReconciliationExportArtifactFile({ runId, exportId: record.id, kind: 'csv' })}>Action CSV</Button>
                  )}
                  {lineageArtifact && (
                    <Button size="small" onClick={() => downloadBillingReconciliationExportArtifactFile({ runId, exportId: record.id, kind: 'lineage' })}>Lineage CSV</Button>
                  )}
                  {zipArtifact && (
                    <Button size="small" onClick={() => downloadBillingReconciliationExportArtifactFile({ runId, exportId: record.id, kind: 'zip' })}>ZIP</Button>
                  )}
                  {manifestArtifact && (
                    <Button size="small" onClick={() => downloadBillingReconciliationExportArtifactFile({ runId, exportId: record.id, kind: 'manifest' })}>Manifest</Button>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export const ExportPreviewTable = ({
  lastValidPage,
  onOpenBac,
  page,
  rowCount,
  rows,
  rowsPerPage,
  setPage,
  setRowsPerPage,
}) => {
  if (rowCount === 0) {
    return <Alert severity="info">No saved visual-builder actions are ready for export.</Alert>;
  }
  if (rows.length === 0) {
    return <Alert severity="info">This export page is empty after the latest refresh; returning to the nearest available page.</Alert>;
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lane</TableCell>
            <TableCell>Exported</TableCell>
            <TableCell>Mutation</TableCell>
            <TableCell>BAC</TableCell>
            <TableCell>Product</TableCell>
            <TableCell align="right">Original</TableCell>
            <TableCell align="right">Proposed</TableCell>
            <TableCell>Binding</TableCell>
            <TableCell>Fix</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const canOpen = Boolean(onOpenBac && row.bac);
            const openRow = () => onOpenBac?.(row.bac, {
              source: 'export_preview',
              severity: row.missingSalesforceAccount || row.missingEvidenceLink ? 'warning' : 'info',
              title: 'Export preview',
              summary: row.fix || row.problem || 'Review export row',
              actionId: row.actionId || row.id,
              focusActionId: row.actionId || row.id,
              productCode: row.productCode,
              productName: row.productName,
            });
            return (
              <TableRow
                key={row.id}
                hover
                tabIndex={canOpen ? 0 : undefined}
                onClick={canOpen ? openRow : undefined}
                onKeyDown={canOpen ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openRow();
                  }
                } : undefined}
                sx={{ cursor: canOpen ? 'pointer' : 'default' }}
              >
                <TableCell>
                  <Typography variant="body2">{laneLabel(row.lane)}</Typography>
                </TableCell>
                <TableCell>
                  <BooleanStatusIcon
                    value={isExportedAction(row)}
                    trueLabel={exportLifecycleLabel(row)}
                    falseLabel={exportLifecycleLabel(row)}
                  />
                </TableCell>
                <TableCell>
                  <MutationIcon mutation={row.mutation} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{row.bac}</TableCell>
                <TableCell>
                  <ProductCodeCell row={row} />
                </TableCell>
                <TableCell align="right">{row.originalPrice === null ? '--' : money(row.originalPrice)}</TableCell>
                <TableCell align="right">{row.proposedPrice === null ? '--' : money(row.proposedPrice)}</TableCell>
                <TableCell>
                  <Tooltip title={bindingHoverLabel(row)} describeChild>
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', ...bindingTextSx(row) }}>
                      {compactBindingLabel(row)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.fix || row.problem || row.note || '--'}</Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rowCount}
        page={Math.min(page, lastValidPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[50, 100, 250]}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
      />
    </TableContainer>
  );
};
